import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Eye, EyeOff, Zap, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { C } from "./theme";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const InputField = ({ label, testId, ...props }) => (
  <div>
    <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: C.textMuted }}>{label}</label>
    <input
      className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors"
      style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
      onFocus={e => e.target.style.borderColor = `${C.primary}60`}
      onBlur={e => e.target.style.borderColor = C.border}
      data-testid={testId}
      {...props}
    />
  </div>
);

const getSubtitle = (mode) => {
  if (mode === "forgot") return "Reset your password";
  if (mode === "reset") return "Create a new password";
  return "Command your autonomous marketing team";
};

const getLoadingText = (mode) => {
  if (mode === "forgot") return "Sending...";
  if (mode === "reset") return "Resetting...";
  if (mode === "login") return "Signing in...";
  return "Creating account...";
};

const AgentAuth = ({ onLogin }) => {
  // mode: "login" | "register" | "forgot" | "reset"
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");

  // Check URL for reset_token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setMode("reset");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login" || mode === "register") {
        const endpoint = mode === "login" ? "/agents/auth/login" : "/agents/auth/register";
        const payload = mode === "login" ? { email, password } : { name, email, password };
        const { data } = await axios.post(`${API}${endpoint}`, payload);
        onLogin(data);
      } else if (mode === "forgot") {
        await axios.post(`${API}/agents/auth/forgot-password`, { email });
        setSuccess("If that email exists, a reset link has been sent. Check your inbox.");
      } else if (mode === "reset") {
        await axios.post(`${API}/agents/auth/reset-password`, { token: resetToken, new_password: newPassword });
        setSuccess("Password reset successfully!");
        setTimeout(() => { setMode("login"); setSuccess(""); setError(""); window.history.replaceState({}, "", window.location.pathname); }, 2000);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `radial-gradient(ellipse at 30% 20%, rgba(245,158,11,0.06) 0%, ${C.bg} 60%)`, fontFamily: "'Inter', sans-serif" }}
    >
      <div className="fixed inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div className="absolute -inset-1 rounded-2xl opacity-20 blur-xl" style={{ background: `linear-gradient(135deg, ${C.primary}33, transparent 70%)` }} />

        <div className="relative rounded-2xl p-8" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full" style={{ background: `${C.primary}15`, border: `1px solid ${C.primary}30` }}>
              <Zap size={14} style={{ color: C.primary }} />
              <span className="text-xs font-medium tracking-wider uppercase" style={{ color: C.primary }}>AI-Powered</span>
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }}>AI Marketing Agents</h1>
            <p className="text-sm" style={{ color: C.textMuted }}>
              {getSubtitle(mode)}
            </p>
          </div>

          {/* Mode toggle - only for login/register */}
          {(mode === "login" || mode === "register") && (
            <div className="flex rounded-xl p-1 mb-6" style={{ background: C.bg }}>
              {[
                { id: "login", label: "Sign In", icon: LogIn },
                { id: "register", label: "Create Account", icon: UserPlus }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => switchMode(id)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    background: mode === id ? C.surface : 'transparent',
                    color: mode === id ? C.text : C.textDim,
                    border: mode === id ? `1px solid ${C.border}` : '1px solid transparent',
                  }}
                  data-testid={`auth-tab-${id}`}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Back button for forgot/reset */}
          {(mode === "forgot" || mode === "reset") && (
            <button
              onClick={() => switchMode("login")}
              className="flex items-center gap-1.5 text-sm mb-5 transition-colors"
              style={{ color: C.textMuted }}
              data-testid="back-to-login-btn"
            >
              <ArrowLeft size={15} />Back to Sign In
            </button>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Register: name field */}
            {mode === "register" && (
              <InputField label="Full Name" testId="auth-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required />
            )}

            {/* Login, Register, Forgot: email field */}
            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <InputField label="Email" testId="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
            )}

            {/* Login, Register: password field */}
            {(mode === "login" || mode === "register") && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: C.textMuted }}>Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={mode === "register" ? "6+ characters" : "Your password"}
                    required
                    minLength={mode === "register" ? 6 : undefined}
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm outline-none transition-colors"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                    onFocus={e => e.target.style.borderColor = `${C.primary}60`}
                    onBlur={e => e.target.style.borderColor = C.border}
                    data-testid="auth-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: C.textDim }}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* Reset: new password field */}
            {mode === "reset" && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: C.textMuted }}>New Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="6+ characters"
                    required
                    minLength={6}
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm outline-none transition-colors"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                    onFocus={e => e.target.style.borderColor = `${C.primary}60`}
                    onBlur={e => e.target.style.borderColor = C.border}
                    data-testid="auth-new-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: C.textDim }}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot password link - only on login */}
            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-xs transition-colors"
                  style={{ color: C.textMuted }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-lg px-4 py-2.5 text-sm"
                style={{ background: `${C.error}15`, border: `1px solid ${C.error}30`, color: C.error }}
                data-testid="auth-error"
              >
                {error}
              </motion.div>
            )}

            {/* Success message */}
            {success && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-lg px-4 py-2.5 text-sm flex items-center gap-2"
                style={{ background: `${C.success}15`, border: `1px solid ${C.success}30`, color: C.success }}
                data-testid="auth-success"
              >
                <CheckCircle2 size={16} />{success}
              </motion.div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: loading ? `${C.primary}80` : C.primary,
                color: '#000',
                boxShadow: loading ? 'none' : `0 0 20px ${C.primary}30`,
              }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  {getLoadingText(mode)}
                </>
              ) : (
                <>
                  {mode === "forgot" && <><KeyRound size={16} />Send Reset Link</>}
                  {mode === "reset" && <><KeyRound size={16} />Reset Password</>}
                  {mode === "login" && "Sign In"}
                  {mode === "register" && "Create Account"}
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AgentAuth;
